import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  reaktiviereDauerauftrag,
  loeseDauerauftragAuf,
  selectKategorien,
} from 'modules/finanzen/features/finanzenSlice';
import { kategorieIcon } from 'modules/finanzen/features/konstanten';
import { istEinnahme } from 'modules/finanzen/features/berechnung';
import { formatKurs } from 'core/utils/format';

/**
 * Archiv der abgelehnten Dauerauftrag-Vorschlaege – standardmaessig zugeklappt.
 * Von hier laesst sich eine Entscheidung zuruecknehmen: „Reaktivieren" macht die
 * Serie aktiv (und verknuepft passende Buchungen), „Entfernen" loescht den
 * Archiv-Eintrag (der Vorschlag kann dann wieder erscheinen).
 */
export default function DauerauftragArchiv({ archiviert }) {
  const [offen, setOffen] = useState(false);
  const eigeneKategorien = useSelector(selectKategorien);

  if (archiviert.length === 0) return null;

  return (
    <section className="mt-6">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        className="flex w-full items-center justify-between rounded-xl border border-line bg-paper-soft px-4 py-3 text-left transition-colors hover:bg-ink/5"
      >
        <span className="text-sm font-medium text-ink-muted">
          🗄️ Archiv (abgelehnt) <span className="text-ink-muted">· {archiviert.length}</span>
        </span>
        <span className="text-ink-muted">{offen ? '▲' : '▼'}</span>
      </button>

      {offen && (
        <ul className="mt-2 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-paper">
          {archiviert.map((d) => (
            <ArchivZeile key={d.uuid} dauerbuchung={d} eigeneKategorien={eigeneKategorien} />
          ))}
        </ul>
      )}
    </section>
  );
}

/** Eine Archiv-Zeile mit Reaktivieren/Entfernen. */
function ArchivZeile({ dauerbuchung, eigeneKategorien }) {
  const dispatch = useDispatch();
  const [laeuft, setLaeuft] = useState(false);
  const einnahme = istEinnahme(dauerbuchung);
  const ton = einnahme ? 'text-gain' : 'text-loss';

  const ausfuehren = async (aktion) => {
    setLaeuft(true);
    try {
      await dispatch(aktion(dauerbuchung.uuid)).unwrap();
    } catch {
      setLaeuft(false);
    }
  };

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <span className="text-xl opacity-60" aria-hidden="true">
        {kategorieIcon(dauerbuchung.typ, dauerbuchung.kategorie, eigeneKategorien)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-ink-muted">
          {dauerbuchung.notiz || dauerbuchung.kategorie}
        </span>
        <span className="block text-xs text-ink-muted">
          {dauerbuchung.kategorie} · {einnahme ? '+' : '−'} {formatKurs(dauerbuchung.betrag)}
        </span>
      </span>
      <button
        type="button"
        onClick={() => ausfuehren(reaktiviereDauerauftrag)}
        disabled={laeuft}
        className="shrink-0 rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-ink transition-colors hover:bg-ink/5 disabled:opacity-50"
      >
        Reaktivieren
      </button>
      <button
        type="button"
        onClick={() => ausfuehren(loeseDauerauftragAuf)}
        disabled={laeuft}
        className="shrink-0 rounded-lg px-2 py-1 text-ink-muted transition-colors hover:bg-ink/5 disabled:opacity-50"
        aria-label="Aus Archiv entfernen"
        title="Aus Archiv entfernen – kann wieder vorgeschlagen werden"
      >
        🗑
      </button>
    </li>
  );
}
